import type {
  IdlePopupHandle,
  IdlePopupStorageKeys,
  IdlePopupTracking,
} from 'hooks/popup/constants';
import { isMCFCountry } from 'lib/region/countryUtils';

export const POPUP_DISMISSED_KEY = 'UH_sizing-kit-popup-dismissed';
export const SIZING_KIT_CLAIMED_KEY = 'UH_sizing-kit-claimed';
export const DEFAULT_TYPEFORM_ID = 'Qry3Lchn';

export type ClaimSizingKitProduct = 'ring_air' | 'ring_pro';
export const DEFAULT_PRODUCT: ClaimSizingKitProduct = 'ring_air';

export const RING_PRO_TYPEFORM_ID = 'Kdk0BeqN';

export type ClaimSizingKitPopupProps = {
  delayMs?: number;
  /** Show after this long with no user activity instead of after `delayMs`. */
  inactivityMs?: number;
  typeformId?: string;
  /** Region code passed as utm_medium tracking param */
  region?: string;
  product?: ClaimSizingKitProduct;
};

export type ClaimSizingKitPopupHandle = IdlePopupHandle;

// Single source of truth for the sizing-kit popup footprint, consumed by
// every mount site (ring/buy IN + AE variants, MCFRing, NonMCFRing,
// ring-pro/buy). Ring AIR: IN/AE/AU + MCF countries minus the exclusions.
// Ring PRO: the same footprint plus CA. ROW never matches by construction.
// US is off (product call, 28 Oct 2026): no sizing-kit popup on US Ring PRO.
const SIZING_KIT_POPUP_BASE_REGIONS = ['IN', 'AE', 'AU'];
const SIZING_KIT_POPUP_EXCLUDED_REGIONS = ['ES', 'FR'];
const RING_PRO_ONLY_SIZING_KIT_REGIONS = ['CA'];

export const shouldShowSizingKitPopup = (
  region: string | undefined,
  product: ClaimSizingKitProduct,
): boolean => {
  const r = region?.toUpperCase() ?? '';
  if (SIZING_KIT_POPUP_EXCLUDED_REGIONS.includes(r)) return false;
  if (SIZING_KIT_POPUP_BASE_REGIONS.includes(r) || isMCFCountry(r)) return true;
  return product === 'ring_pro' && RING_PRO_ONLY_SIZING_KIT_REGIONS.includes(r);
};

export const RING_AIR_POPUP_IMAGE = 'web_v2/sizing-kit/ring-air-sizing-kit.png';

export const RING_PRO_POPUP_IMAGE = 'web_v2/sizing-kit/pro-sizing-kit.png';

export const RING_PRO_POPUP_SAMPLES_IMAGE =
  'web_v2/ring-pro/sizing-kit-popup/pro-sizing-kit.png';

export const storageKeysForProduct = (
  product: ClaimSizingKitProduct,
): IdlePopupStorageKeys => {
  if (product === 'ring_air') {
    return {
      dismissed: POPUP_DISMISSED_KEY,
      completed: SIZING_KIT_CLAIMED_KEY,
    };
  }
  return {
    dismissed: `${POPUP_DISMISSED_KEY}:${product}`,
    completed: `${SIZING_KIT_CLAIMED_KEY}:${product}`,
  };
};

export const SIZING_KIT_POPUP_TRACKING: IdlePopupTracking = {
  analytics: {
    TIMER_STARTED: 'sizing_kit_popup_timer_started',
    SUPPRESSED: 'sizing_kit_popup_suppressed',
    POPUP_SHOWN: 'sizing_kit_popup_shown',
    POPUP_DISMISSED: 'sizing_kit_popup_dismissed',
    COMPLETED: 'sizing_kit_popup_claimed',
    CANCELLED: 'sizing_kit_popup_cancelled',
  },
  gtm: {
    TIMER_STARTED: 'sizingKitPopupTimerStarted',
    SUPPRESSED: 'sizingKitPopupSuppressed',
    POPUP_SHOWN: 'sizingKitPopupShown',
    POPUP_DISMISSED: 'sizingKitPopupDismissed',
    COMPLETED: 'sizingKitPopupClaimed',
    CANCELLED: 'sizingKitPopupCancelled',
  },
};
